import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../../lib/i18n";

const LangToggle = ({ onSwitch }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid="lang-toggle" className="flex items-center rounded-full border border-white/15 overflow-hidden">
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => { setLang(l); if (onSwitch) onSwitch(); }}
          className={`px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${lang === l ? "bg-[--gold] text-[#14180E]" : "text-[--text-2] hover:text-[--text]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Always land at the top of the target page — even if already on it.
  const goPage = (path) => {
    setOpen(false);
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(path);
    }
  };

  const goAnchor = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      document.querySelector(`#${id}`)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-[#14180E]/85 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/40 to-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-[68px] flex items-center justify-between">
        <button onClick={() => goPage("/")} data-testid="nav-logo" className="font-display text-lg md:text-xl tracking-tight text-[--text]">
          Mediterranean <span className="italic text-[--gold]">Palate</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          <button onClick={() => goPage("/")} data-testid="nav-home" className="nav-link font-mono text-[10px] uppercase tracking-[0.22em] text-[--text-2] hover:text-[--text] transition-colors">{t("nav.home")}</button>
          <button onClick={() => goAnchor("about")} className="nav-link font-mono text-[10px] uppercase tracking-[0.22em] text-[--text-2] hover:text-[--text] transition-colors">{t("nav.about")}</button>
          <button onClick={() => goPage("/menus")} data-testid="nav-menus" className="nav-link font-mono text-[10px] uppercase tracking-[0.22em] text-[--text-2] hover:text-[--text] transition-colors">{t("nav.menus")}</button>
          <button onClick={() => goPage("/gallery")} data-testid="nav-gallery" className="nav-link font-mono text-[10px] uppercase tracking-[0.22em] text-[--text-2] hover:text-[--text] transition-colors">{t("nav.gallery")}</button>
          <button onClick={() => goPage("/weddings")} className="nav-link font-mono text-[10px] uppercase tracking-[0.22em] text-[--gold] hover:text-[--text] transition-colors">{t("nav.weddings")}</button>
          <button onClick={() => goPage("/work-with-us")} className="nav-link font-mono text-[10px] uppercase tracking-[0.22em] text-[--text-2] hover:text-[--text] transition-colors">{t("nav.work")}</button>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangToggle />
          <button onClick={() => goPage("/request-a-quote")} data-testid="nav-quote-btn" className="inline-flex px-5 py-2.5 rounded-full bg-[--gold] text-[#14180E] font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[--text] transition-colors duration-400">
            {t("nav.quote")}
          </button>
        </div>

        <button onClick={() => setOpen((v) => !v)} className="lg:hidden text-[--text]" data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="lg:hidden overflow-hidden bg-[#14180E]/95 backdrop-blur-xl border-b border-white/10">
            <div className="px-6 py-6 flex flex-col gap-5">
              <button onClick={() => goPage("/")} className="text-left font-display text-2xl text-[--text]">{t("nav.home")}</button>
              <button onClick={() => goAnchor("about")} className="text-left font-display text-2xl text-[--text]">{t("nav.about")}</button>
              <button onClick={() => goPage("/menus")} data-testid="nav-menus-mobile" className="text-left font-display text-2xl text-[--text]">{t("nav.menus")}</button>
              <button onClick={() => goPage("/gallery")} data-testid="nav-gallery-mobile" className="text-left font-display text-2xl text-[--text]">{t("nav.gallery")}</button>
              <button onClick={() => goPage("/weddings")} className="text-left font-display text-2xl text-[--gold] italic">{t("nav.weddings")}</button>
              <button onClick={() => goPage("/work-with-us")} className="text-left font-display text-2xl text-[--text]">{t("nav.work")}</button>
              <div className="flex items-center gap-4 mt-2">
                <LangToggle />
                <button onClick={() => goPage("/request-a-quote")} data-testid="nav-quote-btn-mobile" className="inline-flex justify-center px-6 py-3 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.18em]">{t("nav.quote")}</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
